import React from 'react';
import {
  FaBox,
  FaChartLine,
  FaDollarSign,
  FaProjectDiagram,
  FaShoppingCart,
} from 'react-icons/fa';

import {
  Box,
  Center,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';

import AnalyticsPage from '../../layout/IntegraAppPage';
import {
  DashboardResumo,
  useDashboardResumo,
} from '../../services/hooks/useDashboard';
import DashboardSkeleton from './DashboardSkeleton';

type DashboardCardProps = {
  resumo: DashboardResumo | null;
  loading: boolean;
};

type StatCardProps = {
  label: string;
  value: string;
  icon: React.ElementType;
  color: string;
};

const formatCurrency = (value?: number | null) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value ?? 0);

const StatCard = ({ label, value, icon, color }: StatCardProps) => {
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const muted = useColorModeValue('#53698a', 'gray.400');
  const valueColor = useColorModeValue('#6b7f9f', 'gray.200');

  return (
    <Box
      bg={bg}
      borderWidth="1px"
      borderColor={border}
      borderRadius="8px"
      boxShadow="0 1px 4px rgba(15, 23, 42, 0.08)"
      minH="168px"
      p="24px"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      <Flex justify="space-between" align="flex-start">
        <Center
          w="44px"
          h="44px"
          borderWidth="2px"
          borderColor={color}
          borderRadius="8px"
          color={color}
        >
          <Icon as={icon} boxSize={5} />
        </Center>

        <HStack spacing={3} align="center">
          <Text color="red.500" fontSize="sm">
            ↘ 0.0000%
          </Text>
        </HStack>
      </Flex>

      <Box>
        <Text color={muted} fontSize="14px" mb={2}>
          {label}
        </Text>
        <Text color={valueColor} fontSize="28px" fontWeight="bold">
          {value}
        </Text>
      </Box>
    </Box>
  );
};

const Panel = ({ children }: { children: React.ReactNode }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');

  return (
    <Box
      bg={bg}
      borderWidth="1px"
      borderColor={border}
      borderRadius="8px"
      boxShadow="0 1px 4px rgba(15, 23, 42, 0.08)"
      w="100%"
      h="100%"
      overflow="hidden"
    >
      {children}
    </Box>
  );
};

const PanelHeader = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) => {
  const titleColor = useColorModeValue('#2D3748', 'gray.100');

  return (
    <Box p={5} borderBottom="1px solid" borderColor="inherit">
      <Text fontSize="22px" fontWeight="bold" color={titleColor}>
        {title}
      </Text>
      <Text fontSize="15px" color="gray.500">
        {subtitle}
      </Text>
    </Box>
  );
};

const PerformanceCard = ({ resumo, loading }: DashboardCardProps) => (
  <Panel>
    <PanelHeader title="Performance Mensal" subtitle="Dados do mês atual" />

    <Box p={5}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text fontSize="15px" color="gray.500">
            Vendas do Mês
          </Text>
          <Text fontSize="30px" fontWeight="bold" color="gray.500">
            {loading ? '...' : formatCurrency(resumo?.vendas_mes)}
          </Text>
        </Box>

        <Text fontSize="16px" color="green.500">
          ↑ 0%
        </Text>
      </Flex>

      <SimpleGrid columns={2} spacing={4}>
        <Box>
          <Text fontSize="15px" color="gray.500">
            Total Pedidos
          </Text>
          <Text fontWeight="bold" color="gray.500">
            {loading ? '...' : resumo?.total_pedidos_mes ?? 0}
          </Text>
        </Box>

        <Box>
          <Text fontSize="15px" color="gray.500">
            Ticket Médio
          </Text>
          <Text fontWeight="bold" color="green.500">
            {loading ? '...' : formatCurrency(resumo?.ticket_medio_mes)}
          </Text>
        </Box>
      </SimpleGrid>
    </Box>
  </Panel>
);

const CanaisCard = ({ resumo, loading }: DashboardCardProps) => {
  const canaisAtivos = resumo?.canais_ativos ?? 0;

  return (
    <Panel>
      <PanelHeader
        title="Canais Conectados"
        subtitle={loading ? '...' : `${canaisAtivos} integrações ativas`}
      />

      <Flex direction="column" align="center" justify="center" p={8}>
        <Center bg="blue.50" color="blue.500" borderRadius="8px" p={4} mb={4}>
          <FaProjectDiagram size={28} />
        </Center>

        <Text fontSize="42px" fontWeight="bold" color="gray.500">
          {loading ? '...' : canaisAtivos}
        </Text>
        <Text color="gray.500">Canais Ativos</Text>

        <Text mt={6} color="purple.500" cursor="pointer">
          Gerenciar canais
        </Text>
      </Flex>
    </Panel>
  );
};

const Dashboard: React.FC = () => {
  const { resumo, loading } = useDashboardResumo();

  if (loading) {
    return <DashboardSkeleton />;
  }

  const stats = [
    {
      label: 'Vendas Hoje',
      value: formatCurrency(resumo?.vendas_hoje),
      icon: FaDollarSign,
      color: '#009879',
    },
    {
      label: 'Pedidos Hoje',
      value: String(resumo?.pedidos_hoje ?? 0),
      icon: FaShoppingCart,
      color: '#1a7fe8',
    },
    {
      label: 'Ticket Médio',
      value: formatCurrency(resumo?.ticket_medio_hoje),
      icon: FaChartLine,
      color: '#009879',
    },
    {
      label: 'Produtos Ativos',
      value: String(resumo?.produtos_ativos ?? 0),
      icon: FaBox,
      color: '#ff3b25',
    },
  ];

  return (
    <AnalyticsPage>
      <Box p={3}>
        <Flex justify="space-between" align="center" mb={4}>
          <Heading fontWeight="bold" fontSize={25}>
            Dashboard
          </Heading>
        </Flex>

        <SimpleGrid columns={[1, 1, 2, 2, 4]} w="100%" spacing={4} mb={6}>
          {stats.map(stat => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mt={2}>
          <PerformanceCard resumo={resumo} loading={loading} />
          <CanaisCard resumo={resumo} loading={loading} />
        </SimpleGrid>
      </Box>
    </AnalyticsPage>
  );
};

export default Dashboard;
